/**
 * Scoring Factors Editor Component
 *
 * Editor for Attack Potential Factor Values (Elapsed Time, Expertise, Equipment, etc.)
 */

import React from 'react';
import { useConfiguration } from '../../../contexts/ConfigurationContext';
import InstructionPanel from '../../../components/ui/InstructionPanel/InstructionPanel';
import { Input } from '../../../components/ui/Input';
import { Slider } from '../../../components/ui/Slider';
import LevelBadge from '../../../components/ui/LevelBadge/LevelBadge';
import { getFeasibilityColor } from '../../../utils/severityStyle';
import styles from './ScoringFactorsEditor.module.css';

const FACTOR_MIN = 0;
const FACTOR_MAX = 19;

/**
 * Map an individual factor-point value (0..19) to one of the four
 * feasibility tiers. The buckets follow the original Tab 6 brackets
 * (0–3 / 4–6 / 7–12 / 13–19) but the colours come from the canonical
 * `getFeasibilityColor` so the bar tints and the Scoring Guidelines
 * labels match every other feasibility surface in the app.
 */
const SCORE_RANGE_LEVELS = [
  { level: 'Very Low', label: '0-3',  description: 'Low difficulty/accessibility' },
  { level: 'Low',      label: '4-6',  description: 'Medium difficulty/accessibility' },
  { level: 'Medium',   label: '7-12', description: 'Medium-High difficulty/accessibility' },
  { level: 'High',     label: '13-19', description: 'High difficulty/accessibility' },
];

const valueToFeasibilityLevel = (value) => {
  if (value >= 13) return 'High';
  if (value >= 7) return 'Medium';
  if (value >= 4) return 'Low';
  return 'Very Low';
};

// IMPORTANT: Keys MUST match backend configuration exactly (backend/src/models/configuration_models.py)
// These keys are used as dictionary keys in the configuration and agent prompt
const FACTORS = [
  {
    key: 'elapsed_time',
    title: 'Elapsed Time',
    description: 'Time required to identify and exploit the vulnerability',
    levels: [
      { key: '<1 day', label: '< 1 day', defaultValue: 0 },
      { key: '<1 week', label: '< 1 week', defaultValue: 1 },
      { key: '<1 month', label: '< 1 month', defaultValue: 4 },
      { key: '<6 months', label: '< 6 months', defaultValue: 17 },
      { key: '>6 months', label: '> 6 months', defaultValue: 19 }
    ]
  },
  {
    key: 'specialist_expertise',
    title: 'Specialist Expertise',
    description: 'Level of expertise required to execute the attack',
    levels: [
      { key: 'Layman', label: 'Layman', defaultValue: 0 },
      { key: 'Proficient', label: 'Proficient', defaultValue: 3 },
      { key: 'Expert', label: 'Expert', defaultValue: 6 },
      { key: 'Multiple experts', label: 'Multiple experts', defaultValue: 8 }
    ]
  },
  {
    key: 'knowledge_of_item',
    title: 'Knowledge of Item',
    description: 'Required knowledge about the target system',
    levels: [
      { key: 'Public', label: 'Public', defaultValue: 0 },
      { key: 'Restricted', label: 'Restricted', defaultValue: 3 },
      { key: 'Confidential', label: 'Confidential', defaultValue: 7 },
      { key: 'Strictly confidential', label: 'Strictly confidential', defaultValue: 11 }
    ]
  },
  {
    key: 'window_of_opportunity',
    title: 'Window of Opportunity',
    description: 'Access window needed to perform the attack',
    levels: [
      { key: 'Unlimited', label: 'Unlimited', defaultValue: 0 },
      { key: 'Easy', label: 'Easy', defaultValue: 1 },
      { key: 'Moderate', label: 'Moderate', defaultValue: 4 },
      { key: 'Difficult/none', label: 'Difficult/none', defaultValue: 10 }
    ]
  },
  {
    key: 'equipment',
    title: 'Equipment',
    description: 'Specialized equipment or tools required',
    levels: [
      { key: 'Standard', label: 'Standard', defaultValue: 0 },
      { key: 'Specialized', label: 'Specialized', defaultValue: 4 },
      { key: 'Bespoke', label: 'Bespoke', defaultValue: 7 },
      { key: 'Multiple bespoke', label: 'Multiple bespoke', defaultValue: 9 }
    ]
  }
];

export default function ScoringFactorsEditor() {
  const { config, updateConfiguration } = useConfiguration();

  if (!config?.configuration?.attack_potential_factors) {
    return <div className={styles.noData}>No scoring factors configuration available</div>;
  }

  const factors = config.configuration.attack_potential_factors;

  const handleValueChange = (factorKey, levelKey, newValue) => {
    const value = parseInt(newValue, 10);
    if (isNaN(value) || value < 0 || value > 19) {
      return;
    }

    const updatedFactors = {
      ...factors,
      [factorKey]: {
        ...factors[factorKey],
        values: {
          ...factors[factorKey].values,
          [levelKey]: value
        }
      }
    };

    const updatedConfig = {
      ...config,
      configuration: {
        ...config.configuration,
        attack_potential_factors: updatedFactors
      }
    };

    updateConfiguration(updatedConfig);
  };

  const getValue = (factorKey, levelKey) => {
    return factors[factorKey]?.values?.[levelKey] || 0;
  };

  const getValueColor = (value) => getFeasibilityColor(valueToFeasibilityLevel(value));

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Attack Potential Scoring Factors</h2>
      </div>

      <div className={styles.factorsContainer}>
        {FACTORS.map((factor) => (
          <div key={factor.key} className={styles.factorCard}>
            <div className={styles.factorHeader}>
              <h3>{factor.title}</h3>
              <p className={styles.factorDescription}>{factor.description}</p>
            </div>

            <div className={styles.levelsGrid}>
              {factor.levels.map((level) => {
                const value = getValue(factor.key, level.key);
                const color = getValueColor(value);

                return (
                  <div key={level.key} className={styles.levelRow}>
                    <div className={styles.levelLabel}>
                      <span>{level.label}</span>
                    </div>
                    <div className={styles.levelInput}>
                      <Input
                        type="number"
                        min={FACTOR_MIN}
                        max={FACTOR_MAX}
                        value={value}
                        onChange={(e) => handleValueChange(factor.key, level.key, e.target.value)}
                        className={styles.input}
                      />
                      <div className={styles.sliderWrap}>
                        <Slider
                          value={[value]}
                          min={FACTOR_MIN}
                          max={FACTOR_MAX}
                          color={color}
                          aria-label={`${factor.title} — ${level.label}`}
                          onValueChange={([next]) => handleValueChange(factor.key, level.key, next)}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Info Box */}
      <div className={styles.infoBox}>
        <h3>Scoring Guidelines</h3>
        <div className={styles.scoreRanges}>
          {SCORE_RANGE_LEVELS.map(({ level, label, description }) => (
            <div key={level} className={styles.scoreRange}>
              <LevelBadge color={getFeasibilityColor(level)} label={label} size="sm" />
              <span>{description}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Help text */}
      <InstructionPanel title="How to use">
        <ul>
          <li>Configure point values (0-19) for each level within each attack potential factor</li>
          <li><strong>Attack Potential Score:</strong> Sum of all factor values for a specific attack path</li>
          <li>Lower values indicate factors that make attacks easier (higher feasibility)</li>
          <li>Higher values indicate factors that make attacks harder (lower feasibility)</li>
          <li><strong>ISO 21434 defaults:</strong> Elapsed Time (0-19), Specialist Expertise (0-8), Knowledge of Item (0-11), Window of Opportunity (0-10), Equipment (0-9)</li>
          <li>Total attack potential scores range from 0-57 (max sum across all factors)</li>
          <li>These scores map to attack feasibility thresholds: Very Low (0-9), Low (10-23), Medium (24-33), High (34-59)</li>
          <li>Changes are saved when you click "Save Changes" in the header</li>
        </ul>
      </InstructionPanel>
    </div>
  );
}
